import { useState, useRef } from 'react'
import { useTranslation } from "react-i18next";
import { ReactComponent as IconTrash } from 'bootstrap-icons/icons/trash.svg'
import { v4 as uuid } from "uuid";
import chunk from 'lodash/chunk'

import useUpload from "hooks/useUpload";
import useMutation from "hooks/useMutation";
import usePlan from "hooks/usePlan";
import { usePlanLock, LockedLabel } from "Components/FeatureBloqueada/PlanLock";

const ALLOWED_FILE_EXTENSIONS = [
  '.jpg', '.jpeg', '.jif', '.jfif', '.png', '.gif', '.webp', '.heic', '.heif', '.pdf',
  '.tiff', '.tif', '.bmp', '.avif', '.dcm', '.dicom', '.ima',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.odt', '.ods', '.odp',
  '.txt', '.csv', '.rtf',
  '.mp4', '.mov', '.avi', '.mkv',
  '.mp3', '.wav', '.m4a',
  '.zip', '.rar', '.7z',
  '.eml', '.msg',
]

function getFileExtension(filename) {
  const match = /\.[^.]+$/.exec(filename || '')
  return match ? match[0].toLowerCase() : ''
}

let FACTOR = 1000

function getSize(bytes) {
  let value = bytes / FACTOR
  if (value < FACTOR) {
    return `${value.toFixed(2)}K`
  }
  value = value / FACTOR
  if (value < FACTOR) {
    return `${value.toFixed(2)}M`
  }
  value = value / FACTOR
  if (value < FACTOR) {
    return `${value.toFixed(2)}G`
  }
  value = value / FACTOR
  if (value < FACTOR) {
    return `${value.toFixed(2)}T`
  }
  value = value / FACTOR
  return `${value.toFixed(2)}P`
}

function Archive(props) {
  const { t } = useTranslation();
  const [state, setState] = useState({ notes: props.file.notes || '', _name: props.file._name || props.file.name || '' })
  let size = getSize(props.file.size)

  const onChange = ({ target: { name, value } }) => {
    state[name] = value
    setState({ ...state })
    props.onChange(name, value)
  }

  return (
    <tr>
      <td className="p-0 w-100">
        <div className="d-flex gap-2">
          <div className="form-floating">
            <input
              type="text"
              name="_name"
              value={state._name || ''}
              onChange={onChange}
              className="form-control border-0"
              placeholder={`${t("ehr:filename", 'Nombre', { context: 'files' })} - ${size}`}
            />
            <label className="text-secondary">{t("ehr:filename", 'Nombre', { context: 'files' })} - {size}</label>
          </div>
          <div className="form-floating border-start flex-fill">
            <input
              type="text"
              name="notes"
              value={state.notes || ''}
              onChange={onChange}
              className="form-control border-0"
              placeholder={t("ehr:notes", 'Notas', { context: 'files' })}
            />
            <label className="text-secondary">{t("ehr:notes", 'Notas', { context: 'files' })}</label>
          </div>
        </div>
      </td>
      <td>
        <button className="btn btn-sm p-2 link-danger" onClick={props.onRemove}>
          <IconTrash />
        </button>
      </td>
    </tr>
  )
}

function Files(props) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([])
  const upload = useUpload()
  const [mutate, mutation] = useMutation(window._l(`/records`).substr(1));
  const [isLoading, setIsLoading] = useState(false)

  const $files = useRef(null)

  const save = async () => {
    setIsLoading(true)
    const array = files.filter(file => !file.createdAt)

    let promises = []
    let hasFailure = false

    try {
      await Promise.all(
        chunk(array, 10).map(async group => {
          const links = await upload(group);

          // upload() already logs and notifies on failure; a null result here means
          // this chunk didn't persist, so keep it in the list for the user to retry.
          if (!links) {
            hasFailure = true
            return
          }

          for (let i = 0; i < links.length; i += 1) {
            const file = group[i]
            file.createdAt = Date.now()

            promises.push(
              mutation.mutateAsync({
                link: links[i],
                consumer: { id: props.consumer.id, label: props.consumer.label },
                createdBy: props.user?.uid,
                createdByName: props.resource?.label || props.user?.name,
                createdByResourceID: props.resource?.id,
                name: file._name || file.name,
                notes: file.notes,
                type: 'files',
                contentType: file.type,
                size: file.size,
              })
            )
          }
        })
      )

      const records = await Promise.all(promises)

      if (props.onSave) props.onSave(records)
    } catch (error) {
      // Bug real (auditoría 2026-08-14): sin try/catch, un mutateAsync
      // rechazado (falla al crear el record de un archivo ya subido a S3)
      // colgaba el spinner "Guardando Archivos" para siempre — setIsLoading
      // y setFiles de abajo nunca corrían, y el modal no tenía forma de
      // cerrarse ni de avisar que algo falló.
      hasFailure = true
      window.notify({
        message: t('ehr:save_files_error', 'No se pudieron guardar algunos archivos — reintentá.'),
        type: 'danger',
        title: 'Error',
      })
    } finally {
      setIsLoading(false)
      setFiles(hasFailure ? [...files] : [])
    }
  }

  const clear = () => {
    setFiles([])
  }

  const onChange = (e) => {
    const array = Array.from(e.target.files)
    $files.current.value = ""

    // multer aborts the whole S3 batch when one file in it fails validation, rolling back
    // files that already uploaded successfully — so a valid + invalid file mixed in the same
    // request lose both. Keep unsupported files out of the batch instead of sending them.
    const allowed = []
    const rejectedNames = []
    for (const item of array) {
      if (ALLOWED_FILE_EXTENSIONS.includes(getFileExtension(item.name))) {
        item.id ||= uuid()
        allowed.push(item)
      } else {
        rejectedNames.push(item.name)
      }
    }

    if (rejectedNames.length > 0) {
      window.notify({
        message: t(
          'ehr:upload_error_unsupported_type_named',
          'Tipo de archivo no permitido: {{files}}',
          { context: 'files', files: rejectedNames.join(', ') }
        ),
        type: 'danger',
        title: 'Error',
      })
    }

    setFiles([...files, ...allowed])
  }

  // hce.files es degradable: en solo lectura se ven los archivos pero no se adjuntan
  // (el back corta la escritura con requireWritableFeature).
  const plan = usePlan();
  const filesLock = usePlanLock("hce.files", {
    blocked: plan.isBlocked("hce.files") || plan.restrictionOf("hce.files") === "read_only",
  });

  return (
    <>
      <button
        data-testid="ehr-files-add"
        onClick={filesLock.guard(() => $files.current.click())}
        className="py-2 px-3 btn btn-sm btn-link d-inline-flex align-items-center gap-1"
      >
        <LockedLabel blocked={filesLock.blocked}>{t('ehr:files', '+ Archivos', { context: 'form' })}</LockedLabel>
      </button>
      {filesLock.modal}
      {files.length > 0 && (
        <div className="position-fixed top-0 start-0 end-0 bottom-0 w-100 h-100 overflow-auto p-3" style={{ zIndex: 1000, background: 'rgba(0, 0, 0, 0.85)' }}>
          {isLoading ? (
            <div
              className="mx-auto bg-white rounded overflow-hidden"
              style={{ width: 'min(768px, calc(100vw - 32px))' }}
            >
              <div className="border-top border-end border-start bg-light p-3 border-bottom">
                <div className="text-secondary lh-1">{t('ehr:saving_files', 'Guardando Archivos')}</div>
              </div>
              <div className="my-3 text-center">
                <div className="spinner-border text-primary">
                  <span className="visually-hidden">{t("loading")}</span>
                </div>
              </div>
            </div>
          ) : (
            // Fix responsive (2026-08-14): 768px fijo (min y max iguales,
            // sin flexibilidad) en el modal de adjuntar archivos a la HCE —
            // más ancho que cualquier celular, solo alcanzable scrolleando
            // el backdrop horizontalmente.
            <div
              className="mx-auto bg-white rounded overflow-hidden"
              style={{ width: 'min(768px, calc(100vw - 32px))' }}
            >
              <div className="d-flex align-items-center border-top border-end border-start bg-light p-1">
                <div>
                  <button className="btn p-2 link-success me-2" onClick={save}>{t('ehr:upload', 'Subir', { context: 'files' })}</button>
                  <button
                    onClick={() => $files.current.click()}
                    className="btn p-2 btn-link"
                    >{t('ehr:files_select_more', 'Seleccionar mas')}
                  </button>
                </div>
                <div className="flex-fill"></div>
                <div>
                  <button className="btn p-2 link-secondary" onClick={clear}>{t('ehr:cancel', 'Cancelar', { context: 'files' })}</button>
                </div>
              </div>
              <table className="table table-bordered m-0">
                <tbody>
                  {files.map(file => (
                    <Archive
                      key={file.id}
                      file={file}
                      onChange={(key, value) => {
                        const array = files.reduce((array, f) => {
                          if (file.id === f.id) {
                            f[key] = value
                          }
                          return [...array, f]
                        }, [])
                        setFiles([...array])
                      }}
                      onRemove={() => setFiles([...files.filter(f => f.id !== file.id)])}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <input
        ref={$files}
        type="file"
        className="form-control d-none"
        id="files"
        name="files[]"
        multiple
        accept={ALLOWED_FILE_EXTENSIONS.join(',')}
        onChange={onChange}
      />
    </>
  )
}

export default Files
